// leadOrigin.ts — lead origin types + header aliases. Mirrors models/origin.go.
//
// Rule 1: very little in a fixed shape (5 fields), the rest in `attributes`.
// Rule 2: filter by IDs, show names. Names change; IDs don't.

import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';

export type Channel = 'meta' | 'google_form' | 'pitchout_form' | 'sheet' | 'csv';

export interface OriginRef {
  id?: string;
  name?: string;
}

export interface LeadOrigin {
  channel?: Channel | string;
  external_id?: string;
  campaign?: OriginRef | null;
  form?: OriginRef | null;
  submitted_at?: string | null;
  attributes?: Record<string, string> | null;
}

export const CHANNEL_LABEL: Record<string, string> = {
  meta: 'Meta Lead Ads',
  google_form: 'Google Form',
  pitchout_form: 'Pitchout Form',
  sheet: 'Google Sheet',
  csv: 'CSV upload',
};
export const channelLabel = (c?: string | null) => (c ? CHANNEL_LABEL[c] ?? c : '—');

/** Friendlier labels for known attribute keys; unknown keys are shown as-is. */
export const ATTR_LABEL: Record<string, string> = {
  ad_id: 'Ad ID',
  ad_name: 'Ad',
  adset_id: 'Ad set ID',
  adset_name: 'Ad set',
  platform: 'Platform',
  is_organic: 'Organic',
  utm_source: 'UTM source',
  utm_medium: 'UTM medium',
  utm_campaign: 'UTM campaign',
  utm_term: 'UTM term',
  utm_content: 'UTM content',
  page_name: 'Page',
};
export const attrLabel = (k: string) => ATTR_LABEL[k] ?? k.replace(/_/g, ' ');

// ── Header aliases (mirror originCoreAliases / originAttrAliases) ─────────────
const CORE: Record<string, string> = {
  id: 'external_id', lead_id: 'external_id', leadgen_id: 'external_id', external_id: 'external_id',
  meta_lead_id: 'external_id', fb_lead_id: 'external_id', response_id: 'external_id',
  campaign_id: 'campaign.id', fb_campaign_id: 'campaign.id',
  campaign_name: 'campaign.name', campaign: 'campaign.name', fb_campaign_name: 'campaign.name',
  form_id: 'form.id', fb_form_id: 'form.id',
  form_name: 'form.name', form: 'form.name', fb_form_name: 'form.name',
  created_time: 'submitted_at', timestamp: 'submitted_at', submitted_at: 'submitted_at',
  submission_time: 'submitted_at', submitted_on: 'submitted_at', lead_created_at: 'submitted_at',
  channel: 'channel', lead_channel: 'channel',
};
const ATTR: Record<string, string> = {
  ad_id: 'ad_id', ad_name: 'ad_name', ad: 'ad_name',
  adset_id: 'adset_id', ad_set_id: 'adset_id', adset_name: 'adset_name', ad_set_name: 'adset_name', adset: 'adset_name',
  platform: 'platform', is_organic: 'is_organic', organic: 'is_organic',
  utm_source: 'utm_source', utm_medium: 'utm_medium', utm_campaign: 'utm_campaign', utm_term: 'utm_term', utm_content: 'utm_content',
  gclid: 'gclid', fbclid: 'fbclid', page_id: 'page_id', page_name: 'page_name',
  partner_name: 'partner_name', retailer_item_id: 'retailer_item_id',
};

const norm = (h: string) => h.trim().toLowerCase().replace(/\s+/g, '_');

/** "campaign.name", "external_id", "attr:ad_name" … or null if not an origin column. */
export const originTarget = (header: string): string | null => {
  const n = norm(header);
  if (CORE[n]) return CORE[n];
  if (ATTR[n]) return `attr:${ATTR[n]}`;
  if (n.startsWith('utm_')) return `attr:${n}`;
  return null;
};

/** Short text for the column-mapping chips. */
export const originTargetLabel = (t: string) => (t.startsWith('attr:') ? `origin · ${t.slice(5)}` : `origin · ${t}`);

// ── Filter options (GET /v1/leads/filter-options?sheet_id=) ────────────────────
export interface FilterOption {
  /** What to send back: spreadsheet ID for sheets, "id:…" / "name:…" for campaigns & forms. */
  value: string;
  /** Latest name — may change, value doesn't. */
  label: string;
  count: number;
  /** Only when no sheet is selected: the sheet this option belongs to. */
  group?: string;
  group_label?: string;
}
export interface LeadFilterOptions {
  campaigns: FilterOption[];
  forms: FilterOption[];
  channels: FilterOption[];
}
export const EMPTY_FILTER_OPTIONS: LeadFilterOptions = { campaigns: [], forms: [], channels: [] };

/** Campaigns / forms of one sheet, or of all sheets (grouped) when sheetId is empty. */
export const fetchLeadFilterOptions = async (sheetId = ''): Promise<LeadFilterOptions> => {
  const q = sheetId ? `?sheet_id=${encodeURIComponent(sheetId)}` : '';
  const res = await apiClient.get<{ success: boolean; data: LeadFilterOptions }>(`/v1/leads/filter-options${q}`);
  return { ...EMPTY_FILTER_OPTIONS, ...(res.data?.data ?? {}) };
};

/** Group options under their sheet name (for <optgroup>), keeping order. */
export const groupOptions = (opts: FilterOption[]): { label: string; options: FilterOption[] }[] => {
  const groups: { key: string; label: string; options: FilterOption[] }[] = [];
  for (const o of opts) {
    const key = o.group ?? '';
    let g = groups.find((x) => x.key === key);
    if (!g) {
      g = { key, label: o.group_label || 'Other', options: [] };
      groups.push(g);
    }
    g.options.push(o);
  }
  return groups;
};

// ── Sheet dropdown (GET /v1/leads/sheet-options) ──────────────────────────────
// 5 most recent sheets, or up to 20 matching a search — never the full list.
export interface SheetOption {
  value: string; // spreadsheet ID
  label: string;
  count: number; // leads from this sheet
  last_synced_at?: string | null;
}
export const fetchSheetOptions = async (search = '', selected = ''): Promise<SheetOption[]> => {
  const p = new URLSearchParams();
  if (search) p.set('search', search);
  if (selected) p.set('selected', selected);
  const res = await apiClient.get<{ success: boolean; data: SheetOption[] }>(`/v1/leads/sheet-options?${p}`);
  return res.data?.data ?? [];
};

// ── Sheets (GET /v1/sheet-imports/sheets) ─────────────────────────────────────
export interface SheetSummary {
  sheet_id: string;
  display_name: string;
  sheet_url: string;
  import_count: number;
  last_synced_at: string | null;
}
export const fetchSheets = async (): Promise<SheetSummary[]> => {
  const res = await apiClient.get<{ success: boolean; data: SheetSummary[] }>('/v1/sheet-imports/sheets');
  return res.data?.data ?? [];
};

/** Rename a sheet everywhere (all its syncs + its beacon). "" clears the name. */
export const renameSheet = (sheetId: string, displayName: string) =>
  apiClient.patch(`/v1/sheet-imports/sheets/${encodeURIComponent(sheetId)}/name`, { display_name: displayName });
